import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { AppError } from '@obraplan/core/application/errors/app-error';
import { ERROR_CODE } from '@obraplan/core/application/errors/error-codes';
import type { User } from '@obraplan/core/domain/access/user';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { profileFormSchema, type ProfileFormValues } from './profile-form';

/**
 * Editar perfil — nome e e-mail.
 *
 * É por aqui que um perfil herdado do uso local ganha e-mail: antes da Fase 12 entrar era
 * escolher um nome numa lista, e quem nasceu assim está sem porta até alguém preencher
 * este campo. Deixar o campo vazio **não apaga** o que já existe.
 *
 * Situação (ativo/inativo) e papéis não estão aqui de propósito: são as ações da linha na
 * tela de perfis, cada uma com sua própria confirmação e seu próprio registro.
 */
export function UserEditDialog({
  open,
  onOpenChange,
  user,
  onSubmit,
  isPending,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | undefined;
  onSubmit: (values: ProfileFormValues) => void;
  isPending: boolean;
  error: AppError | undefined;
}) {
  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: { name: '', email: '' },
  });

  const { reset } = form;

  useEffect(() => {
    if (open && user !== undefined) {
      reset({ name: user.name, email: user.email ?? '' });
    }
  }, [open, user, reset]);

  if (user === undefined) return null;

  const fieldError = (field: string): string | undefined =>
    error?.fieldErrors.find((f) => f.field === field)?.message;

  const nameError =
    form.formState.errors.name?.message ??
    fieldError('name') ??
    (error?.code === ERROR_CODE.USER_NAME_TAKEN ? error.message : undefined);

  const emailError =
    form.formState.errors.email?.message ??
    fieldError('email') ??
    (error?.code === ERROR_CODE.USER_EMAIL_TAKEN ? error.message : undefined);

  // O que não couber em um campo — perfil sumiu, permissão negada — não pode ficar mudo.
  const generalError =
    error !== undefined && nameError === undefined && emailError === undefined
      ? error.message
      : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar perfil</DialogTitle>
          <DialogDescription>
            O nome identifica quem operou o sistema no histórico de alterações, e o e-mail é por
            onde a pessoa entra.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit((values) => onSubmit(values))(event);
          }}
        >
          <Field label="Nome" htmlFor="edit-profile-name" required error={nameError}>
            <Input
              id="edit-profile-name"
              autoFocus
              aria-invalid={nameError !== undefined}
              {...form.register('name')}
            />
          </Field>

          <Field
            label="E-mail"
            htmlFor="edit-profile-email"
            hint={
              user.email === undefined
                ? 'Este perfil não tem e-mail e, por isso, não consegue entrar no sistema.'
                : 'Em branco mantém o e-mail atual — o campo não apaga o que já está gravado.'
            }
            error={emailError}
          >
            <Input
              id="edit-profile-email"
              type="email"
              aria-invalid={emailError !== undefined}
              {...form.register('email')}
            />
          </Field>

          {generalError !== undefined && (
            <p role="alert" className="text-sm text-(--color-state-danger)">
              {generalError}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isPending}>
              {isPending ? 'Salvando…' : 'Salvar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
